<script>
  import { computed, onMounted, ref } from '@vue/composition-api'

  import FormSubmitActions from '@/utils/actions/form_submit_actions'

  export default {
    name: 'ConfigureNotion',
    props: {
      integration: {
        type: Object,
        required: true,
      },
    },
    setup(props, { root, emit }) {
      const formId = root.$route.params.formId
      const selectedDbId = ref(null)
      const sendExisting = ref(true)
      const displayAnswer = ref('answer_values')
      const dbList = ref([])
      const loading = ref(false)
      const mappingRows = ref([])

      const fieldTypeToNotionType = {
        SHORT_ANSWER: ['rich_text', 'title'],
        PARAGRAPH: ['rich_text'],
        TEXT: ['rich_text', 'title'],
        SMART_PARAGRAPH: ['rich_text'],
        DATETIME: ['date', 'rich_text', 'title'],
        EMAIL: ['email'],
        PHONE: ['phone_number'],
        SINGLE_SELECTION: ['select', 'status'],
        DROPDOWN: ['select', 'status'],
        IMAGE_CHOICE: ['select', 'status'],
        MATRIX: ['multi_select'],
        MULTIPLE_SELECTION: ['multi_select'],
        NUMBER: ['number'],
        SLIDER: ['number'],
        OPINION_SCALE: ['number'],
        STAR_RATING: ['number'],
        FILE_UPLOAD: ['files'],
        IMAGE: ['files'],
        SIGNATURE: ['files'],
        ADDRESS: ['rich_text', 'title'],
        MATRIX_OPTION: ['select', 'status'],
        MATRIX_MULTIPLE_OPTION: ['multi_select'],
        MULTIPLE_SELECTION_OPTION: ['checkbox'],
      }

      const formattedQuestions = computed(() => {
        const questions = []
        Object.entries(root.$store.getters['edit/getFormJSON'].fields).forEach(
          ([fieldID, fieldObj]) => {
            if (fieldObj.field_type === 'TEXT') {
              return
            }
            // Add the main question
            questions.push({
              title: fieldObj.title,
              fieldId: fieldID,
              type: fieldObj.field_type,
            })

            // Add matrix rows if it's a matrix question
            if (fieldObj.field_type === 'MATRIX' && fieldObj.content?.rows) {
              fieldObj.content.rows.forEach((row, index) => {
                questions.push({
                  title: `${fieldObj.title} (${row.text})`,
                  fieldId: `${fieldID}_${index}`,
                  type: fieldObj.properties.multiple_selection
                    ? 'MATRIX_MULTIPLE_OPTION'
                    : 'MATRIX_OPTION',
                })
              })
            }

            // Add multiple selection options if it's a multiple selection question
            if (
              fieldObj.field_type === 'MULTIPLE_SELECTION' &&
              fieldObj.content?.options
            ) {
              fieldObj.content.options.forEach((option, index) => {
                questions.push({
                  title: `${fieldObj.title} (${option})`,
                  fieldId: `${fieldID}_${index}`,
                  type: 'MULTIPLE_SELECTION_OPTION',
                })
              })
            }
          }
        )
        return questions
      })

      const questions = computed(() => [
        {
          title: 'Submission ID',
          fieldId: 'submissionID',
          type: 'TEXT',
        },
        {
          title: 'Submission Date',
          fieldId: 'submissionDate',
          type: 'DATETIME',
        },
        ...formattedQuestions.value,
      ])

      const getCompatibleProperties = questionType => {
        if (!selectedDbProperties.value.length) return []
        const compatibleTypes = fieldTypeToNotionType[questionType] || []
        return selectedDbProperties.value.filter(property =>
          compatibleTypes.includes(property.type)
        )
      }

      const getAvailablePropertiesForRow = row => {
        // Get all selected propertyIds except for the current row
        const selectedPropertyIds = mappingRows.value
          .filter(r => r !== row && r.propertyId)
          .map(r => r.propertyId)
        // Get compatible properties for the question type
        const compatible = getCompatibleProperties(
          row.questionId
            ? questions.value.find(q => q.fieldId === row.questionId)?.type
            : null
        )
        // Filter out already selected propertyIds except the current row's propertyId
        return compatible.filter(
          prop =>
            !selectedPropertyIds.includes(prop.id) || prop.id === row.propertyId
        )
      }

      const launchSnackbar = () => {
        root.snackbarEventBus.$emit('snackbar', {
          emoji: root.constants.EMOJI.HANDS_UP,
          color: root.constants.SNACKBAR_CONSTANTS.COLORS.SECONDARY,
          text: 'Changes Successfully Saved',
        })
      }

      const fetchDbList = async () => {
        try {
          const res = await FormSubmitActions.listNotionDatabases(formId)
          dbList.value = res.data.databases
        } catch (err) {
          console.error(err)
        }
      }

      const addMappingRow = () => {
        mappingRows.value.push({
          id: Date.now() + Math.random(),
          questionId: null,
          propertyId: null,
        })
      }

      const removeMappingRow = rowId => {
        const index = mappingRows.value.findIndex(row => row.id === rowId)
        if (index > -1) {
          mappingRows.value.splice(index, 1)
        }
      }

      const saveInfo = async () => {
        loading.value = true
        emit('close-integration')
        launchSnackbar()
        try {
          await FormSubmitActions.useNotionDb(
            selectedDbId.value,
            formId,
            displayAnswer.value === 'answer_values',
            sendExisting.value,
            mappingRows.value.map(({ propertyId, questionId }) => ({
              propertyId,
              questionId,
            }))
          )
        } catch (err) {
          console.error(err)
        } finally {
          loading.value = false
        }
      }

      const selectedDb = computed(() =>
        dbList.value.find(db => db.id === selectedDbId.value)
      )

      const selectedDbProperties = computed(() => {
        if (!selectedDb.value || !selectedDb.value.properties) return []
        return Object.entries(selectedDb.value.properties).map(
          ([name, prop]) => ({
            id: prop.id,
            name,
            type: prop.type.toLowerCase(),
          })
        )
      })

      onMounted(() => {
        if (props.integration.itemId) {
          selectedDbId.value = props.integration.itemId
          sendExisting.value = props.integration.sendExisting
          displayAnswer.value = props.integration.displayAnswers
            ? 'answer_values'
            : 'answer_codes'
          const selectedFields = props.integration.selectedFields || []

          mappingRows.value = selectedFields.map(
            ({ propertyId, questionId }) => {
              return {
                id: Date.now() + Math.random(),
                questionId,
                propertyId,
              }
            }
          )
        }
      })

      fetchDbList()

      return {
        questions,
        selectedDbId,
        sendExisting,
        displayAnswer,
        dbList,
        saveInfo,
        selectedDb,
        loading,
        selectedDbProperties,
        getCompatibleProperties,
        mappingRows,
        addMappingRow,
        removeMappingRow,
        getAvailablePropertiesForRow,
      }
    },
  }
</script>

<template>
  <v-container>
    <v-select
      v-model="selectedDbId"
      :items="dbList"
      item-text="title"
      item-value="id"
      label="Select Notion Database"
      outlined
      required
      dense
    ></v-select>

    <div v-if="selectedDbProperties.length">
      <h3 class="mt-3 mb-5 form-integration-text">Map properties</h3>

      <!-- Mapping rows -->
      <div
        v-for="row in mappingRows"
        :key="row.id"
        class="mt-0"
      >
        <v-row no-gutters>
          <v-col cols="6">
            <v-autocomplete
              v-model="row.questionId"
              :items="questions"
              label="Select Question"
              item-text="title"
              item-value="fieldId"
              clearable
              outlined
              dense
            ></v-autocomplete>
          </v-col>
          <v-col
            cols="1"
            class="d-flex align-center justify-center"
            style="min-height: 56px; margin-top: -1rem"
          >
            <v-icon
              small
              color="gray_500"
              class="my-auto"
            >
              mdi-chevron-double-right
            </v-icon>
          </v-col>
          <v-col cols="4">
            <v-autocomplete
              v-model="row.propertyId"
              :items="getAvailablePropertiesForRow(row)"
              label="Notion Property"
              item-text="name"
              item-value="id"
              clearable
              outlined
              dense
              :disabled="!row.questionId"
            ></v-autocomplete>
          </v-col>
          <v-col
            cols="1"
            class="d-flex align-center pl-0"
            style="margin-top: -1rem"
          >
            <v-btn
              v-if="mappingRows.indexOf(row) > 0"
              icon
              small
              color="gray_500"
              @click="removeMappingRow(row.id)"
            >
              <v-icon small>mdi-trash-can-outline</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Add property button -->
      <v-row
        no-gutters
        justify="end"
        class="mr-3"
      >
        <v-btn
          color="primary"
          outlined
          @click="addMappingRow"
        >
          <v-icon left>mdi-plus</v-icon>
          Add Property
        </v-btn>
      </v-row>

      <h3 class="mt-6 form-integration-text">Row Data Options</h3>
      <v-radio-group
        v-model="displayAnswer"
        class="mb-5"
      >
        <v-radio
          label="Display Answer Values"
          value="answer_values"
          class="mb-3"
        ></v-radio>
        <v-radio
          label="Display Answer Codes/Index"
          value="answer_codes"
        ></v-radio>
      </v-radio-group>
      <div class="group-control__wrapper">
        <f-switch
          v-model="sendExisting"
          class="mr-2"
        />
        Export Existing Data
      </div>
      <v-row justify="start">
        <v-btn
          color="primary"
          height="32"
          class="mt-6 rounded-pill font-weight-medium save-btn"
          :loading="loading"
          :disabled="loading"
          @click="saveInfo"
        >
          Save
        </v-btn>
      </v-row>
    </div>
  </v-container>
</template>

<style lang="scss">
  .save-btn {
    width: 120px !important;
    height: 32px !important;
  }
  .group-control__wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
  }
  .form-integration-text {
    color: var(--v-gray_700-base);
    font-size: 16px;
    font-weight: 500;
  }
  .mapping-row {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    background-color: #fafafa;
  }
</style>
